import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import MercadoPagoPayment from "@/components/MercadoPagoPayment";
import { toast } from "sonner";
import {
  ChevronLeft,
  Scissors,
  CalendarDays,
  Clock,
  User,
  QrCode,
  Store,
  CheckCircle2,
  X,
  ExternalLink,
  RefreshCw,
  Loader2,
} from "lucide-react";

const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const STEPS = ["Serviço", "Data", "Horário", "Seus dados"];
const BRL = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const toISO = (d) => d.toLocaleDateString("sv-SE");

export default function BookingWizard({ services, serviceIds, setServiceIds }) {
  const [step, setStep] = useState(1);
  const [barbers, setBarbers] = useState([]);
  const [barberId, setBarberId] = useState(null);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [time, setTime] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("presencial");
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    if (step > 1) {
      panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [step]);

  useEffect(() => {
    api
      .get("/barbers")
      .then((r) => setBarbers(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!barberId) return;
    setPaymentConfig(null);
    api
      .get(`/payments/config?barber_id=${barberId}`)
      .then((r) => setPaymentConfig(r.data))
      .catch(() => setPaymentConfig(null));
  }, [barberId]);

  const days = useMemo(() => {
    const arr = [];
    const today = new Date();
    for (let i = 0; i < 21; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      const wd = d.getDay();
      if (wd === 0 || wd === 1) continue; // fechado dom/seg
      arr.push(d);
    }
    return arr;
  }, []);

  const loadSlots = useCallback((d, b) => {
    if (!d || !b) return;
    setSlotsLoading(true);
    api
      .get(`/slots?date=${d}&barber_id=${b}`)
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => toast.error("Erro ao carregar horários"))
      .finally(() => setSlotsLoading(false));
  }, []);

  useEffect(() => {
    setTime(null);
    loadSlots(date, barberId);
  }, [date, barberId, loadSlots]);

  const selected = services.filter((s) => serviceIds.includes(s.id));
  const barber = barbers.find((b) => b.id === barberId);
  const total = selected.reduce((sum, s) => sum + s.price, 0);
  const totalDuration = selected.reduce((sum, s) => sum + (s.duration_min || 30), 0);
  const phoneDigits = phone.replace(/\D/g, "");
  const canConfirm = name.trim().length >= 2 && phoneDigits.length >= 8 && !submitting;

  async function confirm() {
    setSubmitting(true);
    try {
      const r = await api.post("/bookings", {
        service_ids: serviceIds,
        barber_id: barberId,
        date,
        time,
        name: name.trim(),
        phone: phoneDigits,
        payment_method: paymentMethod,
      });
      setResult(r.data);
      setStep(5);
      toast.success("Agendamento confirmado!");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail || "Não foi possível concluir o agendamento");
      if (e.response?.status === 409) {
        setTime(null);
        loadSlots(date, barberId);
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function checkPayment() {
    if (!result?.booking?.reference_id) return;
    setChecking(true);
    try {
      const r = await api.get(`/payments/picpay/${result.booking.reference_id}/status`);
      if (r.data.payment_status === "pago") {
        setResult((prev) => ({
          ...prev,
          booking: { ...prev.booking, payment_status: "pago" },
        }));
        toast.success("Pagamento confirmado!");
      } else {
        toast.info("Pagamento ainda não confirmado. Tente novamente em instantes.");
      }
    } catch {
      toast.error("Não foi possível verificar o pagamento");
    } finally {
      setChecking(false);
    }
  }

  function reset() {
    setStep(1);
    setServiceIds([]);
    setBarberId(null);
    setDate(null);
    setTime(null);
    setName("");
    setPhone("");
    setPaymentMethod("presencial");
    setResult(null);
  }

  const dateLabel = (iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    return `${d} de ${MONTHS[m - 1]}`;
  };

  return (
    <section
      id="agendar"
      data-testid="booking-wizard"
      className="mx-auto max-w-md scroll-mt-20 px-4 py-14 md:max-w-2xl lg:max-w-4xl"
    >

      {step <= 4 && (
        <div data-testid="wizard-steps" className="mt-6 flex items-center gap-2">
          {STEPS.map((label, i) => (
            <div key={label} className="flex-1">
              <div
                className={`h-1 rounded-full transition-colors ${
                  step > i ? "bg-amber-500" : "bg-zinc-800"
                }`}
              />
              <p
                className={`mt-1.5 text-[10px] font-semibold uppercase tracking-wider ${
                  step > i ? "text-amber-400" : "text-zinc-600"
                }`}
              >
                {label}
              </p>
            </div>
          ))}
        </div>
      )}

      <div ref={panelRef} className="mt-6 scroll-mt-24 rounded-2xl border border-amber-500/15 bg-[#161618] p-5">
        {step > 1 && step <= 4 && (
          <button
            data-testid="wizard-back-button"
            onClick={() => setStep(step - 1)}
            className="mb-4 flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-zinc-400 transition-colors hover:text-amber-300"
          >
            <ChevronLeft className="h-4 w-4" /> Voltar
          </button>
        )}

        {step === 1 && (
          <div data-testid="step-service">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-amber-200/90">
              <Scissors className="h-5 w-5 text-amber-400" /> Seus serviços
            </h3>
            {serviceIds.length === 0 ? (
              <div className="mt-4 rounded-xl border border-zinc-800 bg-[#0d0d0e] p-5 text-center">
                <p className="text-sm leading-relaxed text-zinc-400">
                  Nenhum serviço selecionado ainda. Toque em{" "}
                  <span className="font-semibold text-amber-300">Selecionar</span> nos cartões
                  acima — pode marcar mais de um.
                </p>
                <button
                  data-testid="go-to-services-button"
                  onClick={() =>
                    document.getElementById("servicos")?.scrollIntoView({ behavior: "smooth" })
                  }
                  className="mt-4 rounded-full border border-amber-500/40 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-amber-300 transition-colors hover:bg-amber-500/10"
                >
                  Ver serviços
                </button>
              </div>
            ) : (
              <div className="mt-4 space-y-2">
                {selected.map((s) => (
                  <div
                    key={s.id}
                    data-testid={`wizard-service-${s.id}`}
                    className="flex w-full items-center justify-between rounded-xl border border-amber-500/30 bg-amber-500/5 p-4"
                  >
                    <div>
                      <p className="text-sm font-semibold text-zinc-100">{s.name}</p>
                      <p className="text-xs text-zinc-500">{s.duration}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-display text-xl text-amber-400">{BRL(s.price)}</span>
                      <button
                        data-testid={`wizard-service-remove-${s.id}`}
                        onClick={() =>
                          setServiceIds((prev) => prev.filter((id) => id !== s.id))
                        }
                        className="flex h-7 w-7 items-center justify-center rounded-full border border-zinc-700 text-zinc-500 transition-colors hover:border-red-500/50 hover:text-red-400"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {serviceIds.length > 0 && (
              <div
                data-testid="services-total"
                className="mt-4 flex items-center justify-between rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-sm"
              >
                <span className="text-zinc-300">
                  {serviceIds.length} serviço{serviceIds.length > 1 ? "s" : ""} · ~
                  {totalDuration} min
                </span>
                <span className="font-display text-xl text-amber-400">{BRL(total)}</span>
              </div>
            )}
            {serviceIds.length > 0 && (
              <div data-testid="barber-inline" className="mt-5 border-t border-amber-500/15 pt-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-amber-200/90">
                  <User className="h-4 w-4 text-amber-400" /> Com quem quer cortar?
                </p>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {barbers.map((b) => {
                    const active = barberId === b.id;
                    return (
                      <button
                        key={b.id}
                        data-testid={`barber-option-${b.id}`}
                        onClick={() => setBarberId(b.id)}
                        className={`flex items-center gap-3 rounded-xl border p-3.5 transition-all active:scale-95 ${
                          active
                            ? "border-amber-500/70 bg-amber-500/10"
                            : "border-zinc-800 bg-[#0d0d0e] hover:border-amber-500/40"
                        }`}
                      >
                        <span
                          className={`font-display flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-lg ${
                            active
                              ? "border-amber-500 bg-amber-500 text-zinc-950"
                              : "border-zinc-700 text-amber-400"
                          }`}
                        >
                          {b.name.charAt(0)}
                        </span>
                        <span className="text-sm font-semibold text-zinc-100">{b.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <button
              data-testid="services-continue-button"
              disabled={serviceIds.length === 0 || !barberId}
              onClick={() => setStep(2)}
              className="mt-4 w-full rounded-full bg-amber-500 py-3.5 text-sm font-bold uppercase tracking-wider text-zinc-950 transition-all hover:bg-amber-400 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
            >
              Continuar
            </button>
          </div>
        )}

        {step === 2 && (
          <div data-testid="step-date">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-amber-200/90">
              <CalendarDays className="h-5 w-5 text-amber-400" /> Escolha o dia
            </h3>
            <p className="mt-1 text-xs text-zinc-500">Aberto de terça a sábado</p>
            <div data-testid="booking-date-picker" className="mt-4 grid grid-cols-4 gap-2">
              {days.map((d, i) => {
                const iso = toISO(d);
                const active = date === iso;
                return (
                  <button
                    key={iso}
                    data-testid={`date-chip-${iso}`}
                    onClick={() => {
                      setDate(iso);
                      setStep(3);
                    }}
                    className={`flex flex-col items-center rounded-xl border px-1 py-3 transition-all active:scale-95 ${
                      active
                        ? "border-amber-500 bg-amber-500/15"
                        : "border-zinc-800 bg-[#0d0d0e] hover:border-amber-500/40"
                    }`}
                  >
                    <span
                      className={`text-[10px] font-bold uppercase tracking-wider ${
                        active ? "text-amber-400" : "text-zinc-500"
                      }`}
                    >
                      {i === 0 ? "Hoje" : i === 1 ? "Amanhã" : WEEKDAYS[d.getDay()]}
                    </span>
                    <span
                      className={`mt-1 text-lg font-bold ${
                        active ? "text-amber-300" : "text-zinc-200"
                      }`}
                    >
                      {d.getDate()}
                    </span>
                    <span className="text-[10px] text-zinc-600">{MONTHS[d.getMonth()]}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="step-time">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-amber-200/90">
              <Clock className="h-5 w-5 text-amber-400" /> Escolha o horário
            </h3>
            {date && (
              <p className="mt-1 text-xs text-zinc-500">{dateLabel(date)}</p>
            )}
            {slotsLoading ? (
              <div
                data-testid="slots-loading"
                className="mt-8 flex items-center justify-center gap-2 py-8 text-sm text-zinc-400"
              >
                <Loader2 className="h-4 w-4 animate-spin text-amber-400" /> Carregando horários...
              </div>
            ) : (
              <div data-testid="time-slot-grid" className="mt-4 grid grid-cols-4 gap-2">
                {slots.map((s) => (
                  <button
                    key={s.time}
                    data-testid={`time-slot-${s.time}`}
                    disabled={!s.available}
                    onClick={() => {
                      setTime(s.time);
                      setStep(4);
                    }}
                    className={`rounded-lg border py-2.5 text-sm font-semibold transition-all ${
                      !s.available
                        ? "cursor-not-allowed border-zinc-800/60 bg-zinc-900/40 text-zinc-700 line-through"
                        : time === s.time
                          ? "border-amber-500 bg-amber-500 text-zinc-950"
                          : "border-zinc-800 bg-[#0d0d0e] text-emerald-400 hover:border-amber-500/50 active:scale-95"
                    }`}
                  >
                    {s.time}
                  </button>
                ))}
              </div>
            )}
            <div className="mt-4 flex items-center gap-4 text-[10px] uppercase tracking-wider text-zinc-500">
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-emerald-400" /> Livre
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-zinc-700" /> Ocupado
              </span>
            </div>
          </div>
        )}

        {step === 4 && (
          <div data-testid="step-details">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-amber-200/90">
              <User className="h-5 w-5 text-amber-400" /> Seus dados
            </h3>
            <div className="mt-4 space-y-3">
              <div>
                <label className="eyebrow !text-[10px]" htmlFor="client-name">
                  Nome
                </label>
                <input
                  id="client-name"
                  data-testid="client-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Seu nome"
                  className="mt-1.5 w-full rounded-xl border border-zinc-800 bg-[#0d0d0e] px-4 py-3 text-sm text-zinc-100 placeholder-zinc-600 outline-none transition-colors focus:border-amber-500/60"
                />
              </div>
              <div>
                <label className="eyebrow !text-[10px]" htmlFor="client-phone">
                  WhatsApp
                </label>
                <input
                  id="client-phone"
                  data-testid="client-phone-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(11) 99999-9999"
                  inputMode="tel"
                  className="mt-1.5 w-full rounded-xl border border-zinc-800 bg-[#0d0d0e] px-4 py-3 text-sm text-zinc-100 placeholder-zinc-600 outline-none transition-colors focus:border-amber-500/60"
                />
              </div>
            </div>

            <p className="eyebrow mt-6 !text-[10px]">Forma de pagamento</p>
            <div className="mt-2 space-y-2">
              <button
                data-testid="payment-option-online"
                disabled={!paymentConfig?.configured}
                onClick={() => setPaymentMethod(paymentConfig.provider)}
                className={`flex w-full items-center gap-3 rounded-xl border p-4 text-left transition-all ${
                  !paymentConfig?.configured
                    ? "cursor-not-allowed border-zinc-800/60 bg-zinc-900/40 opacity-50"
                    : paymentMethod === paymentConfig?.provider
                      ? "border-[#11c76f]/70 bg-[#11c76f]/10"
                      : "border-zinc-800 bg-[#0d0d0e] hover:border-[#11c76f]/40"
                }`}
              >
                <QrCode className="h-5 w-5 shrink-0 text-[#11c76f]" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-zinc-100">
                    Pagar agora via{" "}
                    {paymentConfig?.provider === "mercadopago" ? "Mercado Pago" : "PicPay"}
                  </p>
                  <p className="text-xs text-zinc-500">
                    {paymentConfig?.configured
                      ? paymentConfig?.provider === "mercadopago"
                        ? "Pix ou cartão — o valor cai direto para o barbeiro"
                        : "QR Code ou link — o valor cai direto para o barbeiro"
                      : "Indisponível no momento"}
                  </p>
                </div>
                <span className="rounded-full bg-[#11c76f]/15 px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-[#11c76f]">
                  Online
                </span>
              </button>
              <button
                data-testid="payment-option-presencial"
                onClick={() => setPaymentMethod("presencial")}
                className={`flex w-full items-center gap-3 rounded-xl border p-4 text-left transition-all ${
                  paymentMethod === "presencial"
                    ? "border-amber-500/70 bg-amber-500/10"
                    : "border-zinc-800 bg-[#0d0d0e] hover:border-amber-500/40"
                }`}
              >
                <Store className="h-5 w-5 shrink-0 text-amber-400" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-zinc-100">Pagar na barbearia</p>
                  <p className="text-xs text-zinc-500">Dinheiro, cartão ou Pix no local</p>
                </div>
                <span className="rounded-full bg-amber-500/15 px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-amber-400">
                  No local
                </span>
              </button>
            </div>

            {selected.length > 0 && date && time && (
              <div
                data-testid="booking-summary"
                className="mt-5 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 text-sm"
              >
                {selected.map((s) => (
                  <div key={s.id} className="flex justify-between text-zinc-300">
                    <span>{s.name}</span>
                    <span className="text-zinc-400">{BRL(s.price)}</span>
                  </div>
                ))}
                <div className="mt-2 flex justify-between border-t border-amber-500/20 pt-2">
                  <span className="font-semibold text-zinc-100">Total</span>
                  <span className="font-semibold text-amber-400">{BRL(total)}</span>
                </div>
                <p className="mt-1 text-xs text-zinc-500">
                  {dateLabel(date)} às {time} · ~{totalDuration} min
                  {barber ? ` · com ${barber.name}` : ""}
                </p>
              </div>
            )}

            <button
              data-testid="confirm-booking-button"
              disabled={!canConfirm}
              onClick={confirm}
              className="gold-glow mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-amber-500 py-3.5 text-sm font-bold uppercase tracking-wider text-zinc-950 transition-all hover:bg-amber-400 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? "Confirmando..." : "Confirmar Agendamento"}
            </button>
          </div>
        )}

        {step === 5 && result && (
          <div data-testid="booking-confirmation-modal" className="animate-fade-up">
            <div className="flex flex-col items-center text-center">
              <span className="gold-glow flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/15">
                <CheckCircle2 className="h-8 w-8 text-amber-400" />
              </span>
              <h3 className="font-display mt-4 text-3xl text-amber-100">
                Agendamento confirmado!
              </h3>
              <p className="mt-1 text-sm text-zinc-400">
                Nos vemos em breve, {result.booking.name.split(" ")[0]}.
              </p>
            </div>

            <div className="mt-6 space-y-2 rounded-xl border border-zinc-800 bg-[#0d0d0e] p-4 text-sm">
              <div className="flex justify-between gap-3">
                <span className="text-zinc-500">Serviços</span>
                <span className="text-right font-semibold text-zinc-100">
                  {(result.booking.service_names?.length
                    ? result.booking.service_names
                    : [result.booking.service_name]
                  )
                    .filter(Boolean)
                    .join(" + ")}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Barbeiro</span>
                <span className="font-semibold text-zinc-100">{result.booking.barber_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Data</span>
                <span className="font-semibold text-zinc-100">
                  {dateLabel(result.booking.date)} às {result.booking.time}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Valor</span>
                <span className="font-semibold text-amber-400">{BRL(result.booking.price)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Pagamento</span>
                <span
                  data-testid="payment-status-label"
                  className={`font-semibold ${
                    result.booking.payment_status === "pago"
                      ? "text-emerald-400"
                      : "text-zinc-100"
                  }`}
                >
                  {result.booking.payment_status === "pago"
                    ? result.booking.payment_method === "mercadopago"
                      ? "Pago via Mercado Pago"
                      : "Pago via PicPay"
                    : result.booking.payment_method !== "presencial"
                      ? "Aguardando pagamento"
                      : "Pagar na barbearia"}
                </span>
              </div>
            </div>

            {result.booking.payment_method === "mercadopago" &&
              result.booking.payment_status !== "pago" && (
                <MercadoPagoPayment
                  booking={result.booking}
                  onPaid={() =>
                    setResult((prev) => ({
                      ...prev,
                      booking: { ...prev.booking, payment_status: "pago" },
                    }))
                  }
                />
              )}

            {result.payment?.qrcode?.base64 && (
              <div className="mt-4 flex flex-col items-center rounded-xl border border-[#11c76f]/30 bg-[#0d0d0e] p-4">
                <p className="eyebrow !text-[10px]">Escaneie com o PicPay</p>
                <img
                  data-testid="picpay-qr-code"
                  src={result.payment.qrcode.base64}
                  alt="QR Code PicPay"
                  className="mt-3 h-48 w-48 rounded-lg bg-white p-2"
                />
              </div>
            )}

            {result.booking.payment_url && result.booking.payment_status !== "pago" && (
              <div className="mt-4 space-y-2">
                <a
                  data-testid="picpay-payment-link"
                  href={result.booking.payment_url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-[#11c76f] py-3.5 text-sm font-bold uppercase tracking-wider text-zinc-950 transition-all hover:bg-[#0da058] active:scale-[0.98]"
                >
                  Pagar com PicPay <ExternalLink className="h-4 w-4" />
                </a>
                <button
                  data-testid="check-payment-button"
                  onClick={checkPayment}
                  disabled={checking}
                  className="flex w-full items-center justify-center gap-2 rounded-full border border-zinc-700 py-3 text-xs font-bold uppercase tracking-wider text-zinc-300 transition-colors hover:border-amber-500/50 hover:text-amber-300"
                >
                  <RefreshCw className={`h-4 w-4 ${checking ? "animate-spin" : ""}`} />
                  Já paguei, verificar
                </button>
              </div>
            )}

            {result.booking.payment_method === "picpay" &&
              !result.payment &&
              !result.booking.payment_url && (
                <p
                  data-testid="picpay-unavailable-notice"
                  className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-center text-xs text-amber-200"
                >
                  Pagamento online indisponível no momento. Seu horário está garantido — pague na
                  barbearia.
                </p>
              )}

            <button
              data-testid="new-booking-button"
              onClick={reset}
              className="mt-5 w-full rounded-full border border-amber-500/40 py-3 text-xs font-bold uppercase tracking-wider text-amber-300 transition-colors hover:bg-amber-500/10"
            >
              Fazer outro agendamento
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
